import { instanceHref } from "../../lib/instance.ts";
import { Extension } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";
import { Plugin } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import { Image } from "@tiptap/extension-image";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { Placeholder } from "@tiptap/extension-placeholder";
import { SlashCommands } from "./slashCommands.ts";
import { isEntityLink } from "./entityLinkNavigation.ts";
import { ListBehavior } from "./listBehavior.ts";

const EntityLinkNavigation = Extension.create({
  name: "entityLinkNavigation",
  addProseMirrorPlugins() {
    return [new Plugin({ props: {
      handleClick(_view, _pos, event) {
        const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
        const href = anchor?.getAttribute("href");
        if (!href || !isEntityLink(href, window.location.origin)) return false;
        event.preventDefault();
        // Keep the current draft and collaborative editing session open.
        window.open(href, "_blank", "noopener,noreferrer");
        return true;
      },
    } })];
  },
});

/** Standard Markdown image syntax; the title marks a playable video without raw HTML. */
const Media = Image.extend({
  parseHTML() {
    return [
      ...(this.parent?.() ?? []),
      { tag: "video[src]", getAttrs: (element) => ({ src: element.getAttribute("src"), alt: element.getAttribute("aria-label") ?? "", title: "tern-video" }) },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    HTMLAttributes = { ...HTMLAttributes, src: instanceHref(String(HTMLAttributes.src ?? "")) };
    if (HTMLAttributes.title === "tern-video") return ["video", { src: HTMLAttributes.src, "aria-label": HTMLAttributes.alt, controls: true, preload: "metadata", contenteditable: "false" }];
    return ["img", HTMLAttributes];
  },
  renderMarkdown(node) {
    const alt = String(node.attrs?.alt ?? "").replace(/\\/g, "\\\\").replace(/\[/g, "\\[").replace(/\]/g, "\\]").replace(/[\r\n]/g, " ");
    const title = String(node.attrs?.title ?? "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return `![${alt}](${node.attrs?.src ?? ""}${title ? ` "${title}"` : ""})`;
  },
});

// GFM always has a header. Promote the next row when its header is deleted so
// saving/reopening does not silently add an extra empty row to the table.
const MarkdownTable = Table.extend({
  addProseMirrorPlugins() {
    return [...(this.parent?.() ?? []), new Plugin({
      appendTransaction(transactions, _old, state) {
        if (!transactions.some((tr) => tr.docChanged)) return null;
        const tr = state.tr;
        state.doc.descendants((node, pos) => {
          if (node.type.name !== "table" || !node.firstChild) return;
          node.firstChild.forEach((cell, offset) => {
            if (cell.type.name === "tableCell") tr.setNodeMarkup(pos + 2 + offset, state.schema.nodes.tableHeader, cell.attrs);
          });
        });
        return tr.docChanged ? tr : null;
      },
    })];
  },
});

/** Parse plain-text Markdown pastes; ProseMirror still handles rich HTML and code blocks. */
const MarkdownPaste = Extension.create({
  name: "markdownPaste",
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [new Plugin({
      props: {
        clipboardTextParser(text, context, plain) {
          if (!plain && !editor.isActive("code") && editor.markdown) {
            const content = editor.schema.nodeFromJSON(editor.markdown.parse(text));
            return Slice.maxOpen(content.content);
          }
          // Shift-paste deliberately keeps Markdown symbols as ordinary text.
          const paragraphs = text.replace(/\r\n?/g, "\n").split("\n").map((line) =>
            editor.schema.nodes.paragraph!.create(null, line ? editor.schema.text(line, context.marks()) : undefined),
          );
          return Slice.maxOpen(Fragment.fromArray(paragraphs));
        },
      },
    })];
  },
});

/** The same Markdown schema and typing rules for descriptions, comments, and Docs. */
export function markdownExtensions(placeholder: string, collaborative = false, slashPlaceholder = placeholder) {
  return [
    StarterKit.configure({
      undoRedo: collaborative ? false : {},
      underline: false,
      link: { openOnClick: false, autolink: true, protocols: ["tern"], HTMLAttributes: { rel: "noopener noreferrer" } },
    }),
    Markdown.configure({ markedOptions: { gfm: true } }),
    MarkdownPaste,
    Media,
    SlashCommands,
    EntityLinkNavigation,
    MarkdownTable.configure({ resizable: false, renderWrapper: true }),
    TableRow,
    TableHeader,
    TableCell,
    TaskList,
    TaskItem.configure({ nested: true }),
    ListBehavior,
    Placeholder.configure({ placeholder: ({ editor, node, pos }) => {
      const parent = editor.state.doc.resolve(pos).parent.type.name;
      if (node.type.name !== "paragraph" || parent === "tableCell" || parent === "tableHeader") return "";
      return editor.isEmpty ? placeholder : slashPlaceholder;
    } }),
  ];
}
